/**
 * Vendor-level reactor designs — v2 catalog (extends generic classes).
 */
import type { ReactorDesignClassId } from './reactorDesignCatalog';

export type ReactorVendorId = 'nuscale-voygr' | 'xe-100' | 'bwrx-300';

export interface ReactorVendorEntry {
  id: ReactorVendorId;
  label: string;
  vendorName: string;
  parentClass: ReactorDesignClassId;
  typicalModuleMWe: number;
  /** NRC docket or subapplication number for ADAMS docket fetch. */
  nrcDocketId?: string;
  /** Curated supplier directory ids (many-to-many). */
  supplierIds: readonly string[];
  licensingNotes?: string;
  source: 'baseline' | 'admin' | 'research-queue';
}

export const REACTOR_VENDOR_CATALOG: Record<ReactorVendorId, ReactorVendorEntry> = {
  'nuscale-voygr': {
    id: 'nuscale-voygr',
    label: 'NuScale VOYGR',
    vendorName: 'NuScale Power',
    parentClass: 'lwr-smr',
    typicalModuleMWe: 77,
    nrcDocketId: '99902072',
    supplierIds: ['supplier-nuscale', 'supplier-areva-framatome'],
    licensingNotes: 'Part 52 COL subapplication; source-term EPZ reduction under review.',
    source: 'baseline',
  },
  'xe-100': {
    id: 'xe-100',
    label: 'X-energy Xe-100',
    vendorName: 'X-Energy',
    parentClass: 'htgr',
    typicalModuleMWe: 80,
    nrcDocketId: '99902073',
    supplierIds: ['supplier-x-energy'],
    licensingNotes: 'HTGR / TRISO; HALEU fuel cycle.',
    source: 'baseline',
  },
  'bwrx-300': {
    id: 'bwrx-300',
    label: 'GE Hitachi BWRX-300',
    vendorName: 'GE Hitachi Nuclear Energy',
    parentClass: 'lwr-smr',
    typicalModuleMWe: 300,
    nrcDocketId: '99902074',
    supplierIds: ['supplier-ge-hitachi', 'supplier-ge-vernova'],
    licensingNotes: 'BWR SMR; larger module block sizing vs NuScale-class.',
    source: 'baseline',
  },
};

export const REACTOR_VENDOR_IDS = Object.keys(REACTOR_VENDOR_CATALOG) as ReactorVendorId[];

export function getReactorVendor(id: string | undefined): ReactorVendorEntry | null {
  if (!id || !(id in REACTOR_VENDOR_CATALOG)) return null;
  return REACTOR_VENDOR_CATALOG[id as ReactorVendorId];
}

export function vendorsForClass(classId: ReactorDesignClassId | undefined): ReactorVendorEntry[] {
  if (!classId || classId === 'undecided') return [];
  return REACTOR_VENDOR_IDS
    .map(vid => REACTOR_VENDOR_CATALOG[vid])
    .filter(v => v.parentClass === classId);
}

export function resolveReactorVendorFromIntent(
  intent: { reactorDesignClass?: ReactorDesignClassId; reactorVendorId?: string } | null | undefined,
): ReactorVendorEntry | null {
  if (!intent?.reactorVendorId) return null;
  const vendor = getReactorVendor(intent.reactorVendorId);
  if (!vendor) return null;
  if (intent.reactorDesignClass && intent.reactorDesignClass !== vendor.parentClass) {
    return null;
  }
  return vendor;
}
